import { useMemo, useState, useEffect } from 'react';
import { TrainFront, MapPin, Power, RefreshCw, ChevronRight, Hand, Loader2, Search } from 'lucide-react';
import { localApi } from '../utils/localApi';

interface Stop {
    stationName: string;
    arrivalTime: string;
    pk: number;
}

interface Mission {
    id: string;
    trainNumber: string;
    lineName: string;
    stops: Stop[];
}

interface ConductorViewProps {
    activeMission: Mission | null;
    missions: Mission[];
    setActiveMission: (m: Mission | null) => void;
    currentTime: Date;
    displayName: string;
    onLogout: () => void;
    displayStatus: any;
    requestedStops: string[];
    stopRequests: { station_name: string; status: string }[];
    toggleRequestedStop: (stationName: string) => Promise<void>;
}

export default function ConductorView({
    activeMission,
    missions,
    setActiveMission,
    currentTime,
    displayName,
    onLogout,
    displayStatus,
    requestedStops,
    stopRequests,
    toggleRequestedStop
}: ConductorViewProps) {
    // État local de loading pour l'UI, mais les données viennent du parent
    const [isActionInProgress, setIsActionInProgress] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isEmergencyActive, setIsEmergencyActive] = useState(false);

    useEffect(() => {
        if (!activeMission) return;
        
        const fetchEmergency = async () => {
            try {
                const res = await localApi.checkEmergencyStop(activeMission.trainNumber);
                setIsEmergencyActive(!!res.requested);
            } catch (err) {
                console.error("Erreur check emergency:", err);
            }
        };

        fetchEmergency();
        const interval = setInterval(fetchEmergency, 3000);
        return () => clearInterval(interval);
    }, [activeMission]);

    const handleEmergencyToggle = async () => {
        if (!activeMission) return;
        setIsActionInProgress(true);
        try {
            if (isEmergencyActive) {
                await localApi.cancelEmergencyStop(activeMission.trainNumber);
                setIsEmergencyActive(false);
            } else {
                await localApi.triggerEmergencyStop(activeMission.trainNumber, displayName);
                setIsEmergencyActive(true);
            }
        } catch (err) {
            console.error("Erreur toggle emergency:", err);
        } finally {
            setIsActionInProgress(false);
        }
    };

    const filteredMissions = useMemo(() => {
        return missions.filter(m =>
            m.trainNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
            m.lineName.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [missions, searchQuery]);

    const handleToggle = async (stationName: string) => {
        setIsActionInProgress(true);
        try {
            await toggleRequestedStop(stationName);
        } finally {
            setIsActionInProgress(false);
        }
    };

    const timeStatus = useMemo(() => {
        if (!displayStatus || !displayStatus.time) return { color: 'text-slate-400', label: '--', diff: 0 };

        const [hours, minutes] = displayStatus.time.split(':').map(Number);
        const expectedTime = new Date();
        expectedTime.setHours(hours, minutes, 0, 0);

        const diffMs = currentTime.getTime() - expectedTime.getTime();
        const diffMins = Math.round(diffMs / (1000 * 60));

        if (diffMins > 3) return { color: 'text-red-500', label: `RETARD ${diffMins} MIN`, diff: diffMins };
        if (diffMins < -3) return { color: 'text-yellow-400', label: `AVANCE ${Math.abs(diffMins)} MIN`, diff: diffMins };
        return { color: 'text-emerald-500', label: 'À L\'HEURE', diff: diffMins };
    }, [currentTime, displayStatus]);

    if (!activeMission) {
        return (
            <div className="h-screen bg-[#020617] flex flex-col p-6 text-white">
                <header className="flex justify-between items-center mb-10">
                    <div>
                        <h1 className="text-2xl font-black italic uppercase tracking-tighter">
                            ASTA <span className="text-cyan-500">RAIL</span>
                        </h1>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Chef de Train : {displayName}</p>
                    </div>
                    <button onClick={onLogout} className="p-3 bg-red-500/10 rounded-2xl text-red-500 border border-red-500/20 active:scale-95 transition-all">
                        <Power size={20} />
                    </button>
                </header>

                <main className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
                    <div className="text-center mb-8">
                        <div className="w-20 h-20 bg-cyan-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-cyan-500/20">
                            <TrainFront className="text-cyan-500" size={40} />
                        </div>
                        <h2 className="text-xl font-bold uppercase tracking-tight">Sélection de Mission</h2>
                        <p className="text-slate-400 text-sm mt-2">Choisissez votre train pour commencer le service.</p>
                    </div>

                    {/* Dispositif de recherche premium */}
                    <div className="relative mb-6">
                        <input
                            type="text"
                            placeholder="Rechercher par train ou destination..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-slate-900/60 border border-cyan-500/20 focus:border-cyan-500/50 rounded-2xl py-3.5 pl-11 pr-10 text-xs sm:text-sm text-white placeholder-slate-500 outline-none transition-all"
                        />
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-500/60 w-4 h-4" />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white text-xs font-bold transition-colors"
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
                        {filteredMissions.map(m => (
                            <button
                                key={m.id}
                                onClick={() => setActiveMission(m)}
                                className="w-full flex items-center justify-between p-5 bg-slate-900/50 border border-white/5 rounded-3xl hover:bg-slate-800/80 transition-all active:scale-95 group"
                            >
                                <div className="text-left">
                                    <p className="text-[10px] text-cyan-500 font-black uppercase tracking-widest mb-1">Train {m.trainNumber}</p>
                                    <p className="text-white font-bold text-sm uppercase">{m.lineName}</p>
                                </div>
                                <ChevronRight size={20} className="text-slate-600 group-hover:text-cyan-500 transition-colors" />
                            </button>
                        ))}
                        {filteredMissions.length === 0 && (
                            <p className="text-slate-500 text-sm text-center italic py-4">Aucune mission trouvée pour "{searchQuery}"</p>
                        )}
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="h-screen bg-[#020617] flex flex-col text-white overflow-hidden">
            {/* HEADER */}
            <header className="px-6 py-6 bg-slate-900/50 backdrop-blur-md border-b border-white/5 flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-cyan-500/10 rounded-2xl flex items-center justify-center border border-cyan-500/20">
                        <TrainFront className="text-cyan-500" size={24} />
                    </div>
                    <div>
                        <h2 className="text-lg font-black uppercase leading-none">TR {activeMission.trainNumber}</h2>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">{activeMission.lineName}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setActiveMission(null)}
                        title="Changer de mission"
                        className="p-3 bg-slate-800 rounded-2xl text-slate-400 border border-white/5 active:scale-95 transition-all"
                    >
                        <RefreshCw size={20} />
                    </button>
                    <button onClick={onLogout} className="p-3 bg-red-500/10 rounded-2xl text-red-500 border border-red-500/20 active:scale-95 transition-all">
                        <Power size={20} />
                    </button>
                </div>
            </header>

            {/* MAIN CONTENT */}
            <main className="flex-1 overflow-y-auto p-6 space-y-6">

                {/* EMERGENCY STOP BUTTON */}
                <div className="bg-slate-900/80 rounded-[2.5rem] p-6 border border-white/5 shadow-2xl flex flex-col items-center">
                    <button
                        onClick={handleEmergencyToggle}
                        disabled={isActionInProgress}
                        className={`w-full py-5 rounded-[2rem] font-black text-sm uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-3 active:scale-95 border ${
                            isEmergencyActive 
                                ? 'bg-red-600 hover:bg-red-500 text-white border-red-500 shadow-[0_0_30px_rgba(220,38,38,0.6)] animate-pulse' 
                                : 'bg-red-950/20 hover:bg-red-950/40 text-red-500 border-red-900/40'
                        }`}
                    >
                        🚨 {isEmergencyActive ? "Annuler demande d'arrêt" : "Demande d'arrêt immédiat"}
                    </button>
                    <p className="text-[9px] text-slate-500 uppercase font-black tracking-widest mt-2 text-center">
                        {isEmergencyActive ? "Signal de détresse en cours de transmission..." : "Actionnez en cas d'urgence pour alerter le conducteur."}
                    </p>
                </div>

                {/* TIME CARD */}
                <div className="bg-slate-900/80 rounded-[2.5rem] p-8 border border-white/5 flex flex-col items-center justify-center shadow-2xl relative overflow-hidden">
                    <p className="text-[10px] text-slate-500 font-black uppercase tracking-[0.3em] mb-4 z-10">Heure Actuelle</p>
                    <p className="text-6xl font-mono font-black tracking-tighter mb-2 z-10 text-white">
                        {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    {timeStatus.diff > 3 && (
                        <p className="text-red-500 text-xs font-black uppercase tracking-widest mb-4 z-10 animate-pulse">
                            Estim. Retard : +{timeStatus.diff} min
                        </p>
                    )}

                    <div className={`px-4 py-2 rounded-full border ${timeStatus.color.replace('text-', 'bg-').replace('500', '500/10')} ${timeStatus.color.replace('text-', 'border-').replace('500', '500/30')} flex items-center gap-2 z-10`}>
                        <div className={`w-2 h-2 rounded-full ${timeStatus.color.replace('text-', 'bg-')} animate-pulse`} />
                        <span className={`text-xs font-black uppercase tracking-widest ${timeStatus.color}`}>{timeStatus.label}</span>
                    </div>
                </div>

                {/* NEXT STOP CARD */}
                <div className={`rounded-[2.5rem] p-8 shadow-xl transition-all duration-500 relative overflow-hidden group 
                    ${requestedStops.some(s => s.toLowerCase() === (displayStatus?.stationName || '').toLowerCase()) ? 'bg-blue-600 animate-pulse' : 'bg-cyan-600'}`}>
                    <div className="absolute -bottom-6 -right-6 text-white/10 group-hover:scale-110 transition-transform duration-700">
                        <MapPin size={160} />
                    </div>

                    <p className="text-[10px] text-white/60 font-black uppercase tracking-[0.3em] mb-4 relative z-10">Prochain Arrêt</p>
                    <p className="text-3xl font-black uppercase tracking-tight text-white mb-2 relative z-10 leading-tight">
                        {displayStatus?.stationName || 'En Ligne'}
                    </p>
                    <div className="flex items-center gap-2 relative z-10">
                        <span className="text-white/80 font-mono text-xl">{displayStatus?.time || '--:--'}</span>
                        {requestedStops.some(s => s.toLowerCase() === (displayStatus?.stationName || '').toLowerCase()) && (
                            <span className="text-[10px] bg-white text-blue-600 px-2 py-0.5 rounded-md font-bold uppercase tracking-widest animate-bounce">Arrêt Demandé</span>
                        )}
                    </div>
                </div>

                {/* STOP LIST */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between px-2">
                        <div className="flex items-center gap-2">
                            <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">Liste des Arrêts</h3>
                            {isActionInProgress && <Loader2 size={12} className="text-slate-500 animate-spin" />}
                        </div>
                        <div className="px-2 py-1 bg-blue-500/10 rounded-lg border border-blue-500/20">
                            <span className="text-[9px] font-bold text-blue-400 uppercase tracking-widest">Sélect. Multiple Active</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                        {activeMission.stops.map((stop, idx) => {
                            const reqObj = stopRequests.find(r => r.station_name.trim().toLowerCase() === stop.stationName.trim().toLowerCase());
                            const status = reqObj?.status || '';
                            const isRequested = !!reqObj;

                            let btnStyle = 'bg-slate-900/40 border-white/5 hover:border-white/10';
                            let accentStyle = 'bg-transparent group-hover:bg-slate-700';
                            let iconStyle = 'bg-slate-800 text-slate-400 group-hover:bg-slate-700';

                            if (isRequested) {
                                if (status === 'accepted') {
                                    btnStyle = 'bg-emerald-500/10 border-emerald-500/30 shadow-[0_0_20px_rgba(16,185,129,0.2)] scale-[1.01]';
                                    accentStyle = 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.8)]';
                                    iconStyle = 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20';
                                } else if (status === 'rejected') {
                                    btnStyle = 'bg-red-500/10 border-red-500/30 scale-[1.01]';
                                    accentStyle = 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.8)]';
                                    iconStyle = 'bg-red-500/10 text-red-400 border border-red-500/20';
                                } else {
                                    // pending
                                    btnStyle = 'bg-blue-500 border-blue-300 shadow-[0_0_30px_rgba(59,130,246,0.5)] scale-[1.02]';
                                    accentStyle = 'bg-white';
                                    iconStyle = 'bg-white text-blue-600';
                                }
                            }

                            return (
                                <button
                                    key={`${stop.stationName}-${idx}`}
                                    onClick={() => handleToggle(stop.stationName)}
                                    className={`flex items-center justify-between p-5 border rounded-3xl transition-all active:scale-[0.98] group relative overflow-hidden ${btnStyle}`}
                                >
                                    <div className={`absolute inset-y-0 left-0 w-1.5 transition-all ${accentStyle}`} />
                                    <div className="flex items-center gap-4">
                                        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-colors ${iconStyle}`}>
                                            <Hand size={18} className={isRequested && status === 'pending' ? 'animate-bounce' : ''} />
                                        </div>
                                        <div className="text-left">
                                            <p className={`text-sm font-black uppercase tracking-tight transition-colors ${isRequested && status === 'pending' ? 'text-white' : 'text-slate-200'}`}>
                                                {stop.stationName}
                                            </p>
                                            <p className={`text-[10px] font-mono uppercase ${isRequested && status === 'pending' ? 'text-blue-100' : 'text-slate-500'}`}>
                                                {stop.arrivalTime} • PK {stop.pk.toFixed(3)}
                                            </p>
                                        </div>
                                    </div>
                                    {isRequested && (
                                        <div className="flex items-center gap-2">
                                            {status === 'accepted' && (
                                                <span className="text-[8px] font-black text-emerald-400 uppercase tracking-widest bg-emerald-500/10 px-2 py-1 rounded-md border border-emerald-500/20">Validé</span>
                                            )}
                                            {status === 'rejected' && (
                                                <span className="text-[8px] font-black text-red-400 uppercase tracking-widest bg-red-500/10 px-2 py-1 rounded-md border border-red-500/20">Refusé</span>
                                            )}
                                            {status === 'pending' && (
                                                <span className="text-[8px] font-black text-white uppercase tracking-widest bg-blue-500 px-2 py-1 rounded-md">Demandé</span>
                                            )}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="p-6 bg-slate-900/30 rounded-3xl border border-white/5">
                    <p className="text-[9px] text-slate-500 font-bold uppercase tracking-[0.2em] text-center">Base de Données ASTA Synchronisée</p>
                </div>

            </main>

        </div>
    );
}
